import { safeShareReturn } from '@/features/sharing/types'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation } from 'react-router'

import { useLogin } from '@/api/hooks'
import { Button, Input } from '@/components/ui'

import { AuthErrorBanner } from './AuthErrorBanner'
import { AuthLayout } from './AuthLayout'
import { fieldErrorOf, generalErrorOf } from './authErrors'
import { PasswordInput } from './PasswordInput'

/** Inputs that render their own inline error, so the banner does not repeat them. */
const HANDLED_FIELDS = ['email', 'password'] as const

export function LoginPage() {
  const login = useLogin()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const error = login.isError ? login.error : null
  const bannerMessage = generalErrorOf(error, HANDLED_FIELDS)
  const canSubmit = email.trim().length > 0 && password.length > 0

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canSubmit || login.isPending) return
    login.mutate({ email: email.trim(), password })
  }

  if (login.isSuccess) return <Navigate to={safeShareReturn(location.state?.from)} replace />

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Your music, on your own server."
      footer={
        <>
          New here?{' '}
          <Link
            to="/register" state={{ from: safeShareReturn(location.state?.from) }}
            className="inline-flex min-h-11 items-center px-1 font-medium text-accent hover:underline"
          >
            Create an account
          </Link>
        </>
      }
    >
      {/* noValidate: required stays for assistive tech, but validation messages are
          ours and inline rather than the browser's floating bubbles. */}
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {bannerMessage ? <AuthErrorBanner message={bannerMessage} /> : null}

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          disabled={login.isPending}
          error={fieldErrorOf(error, 'email')}
        />

        <PasswordInput
          label="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
          disabled={login.isPending}
          error={fieldErrorOf(error, 'password')}
        />

        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={login.isPending}
          disabled={!canSubmit}
          className="mt-1"
        >
          Sign in
        </Button>
      </form>
    </AuthLayout>
  )
}
